"use client";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import { AgendaDay, AgendaSchedule, ResourceType } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { dateParseISO } from "@/lib/utils";

interface MobileDayStripProps {
  weekDate: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  days: string[];
  selectedDayIndex: number;
  onSelectDay: (index: number) => void;
  agenda: Record<string, AgendaDay> | undefined;
}

// Workday is split into 4 segments: 8-10:30, 10:30-13, 13-15:30, 15:30-18
const SEGMENT_BOUNDARIES = [8, 10.5, 13, 15.5, 18];

const getTimeBlockSegments = (
  schedules: AgendaSchedule[] | undefined
): boolean[] => {
  if (!schedules || schedules.length === 0) return [false, false, false, false];

  const segments = [false, false, false, false];

  for (const schedule of schedules) {
    if (!schedule.start || !schedule.end) continue;

    const startDate = new Date(schedule.start);
    const endDate = new Date(schedule.end);
    const startHour = startDate.getHours() + startDate.getMinutes() / 60;
    const endHour = endDate.getHours() + endDate.getMinutes() / 60;

    for (let i = 0; i < 4; i++) {
      const segStart = SEGMENT_BOUNDARIES[i];
      const segEnd = SEGMENT_BOUNDARIES[i + 1];
      if (startHour < segEnd && endHour > segStart) {
        segments[i] = true;
      }
    }
  }

  return segments;
};

const getScheduleDotColor = (type: ResourceType | undefined): string => {
  if (type === "meeting_room") return "bg-violet-500";
  if (type === "desk" || type === "workspace") return "bg-primary";
  return "bg-neutral-400";
};

const getScheduleDots = (schedules: AgendaSchedule[] | undefined): string[] => {
  if (!schedules || schedules.length === 0) return [];
  // Cap at 3 dots to keep it compact
  return schedules
    .slice(0, 3)
    .map((s) => getScheduleDotColor(s.resource?.type));
};

export const MobileDayStrip: React.FC<MobileDayStripProps> = ({
  weekDate,
  onPrevWeek,
  onNextWeek,
  days,
  selectedDayIndex,
  onSelectDay,
  agenda
}) => {
  return (
    <div className="mx-4 rounded-2xl border border-neutral-100 bg-white shadow-sm">
      {/* Month header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <IconButton
          icon={ChevronLeft}
          variant="ghost"
          size="md"
          iconClassName="h-4 w-4"
          onClick={onPrevWeek}
          aria-label="Previous week"
        />
        <h2 className="font-semibold text-neutral-900 text-sm">
          {format(dateParseISO(weekDate), "MMMM yyyy")}
        </h2>
        <IconButton
          icon={ChevronRight}
          variant="ghost"
          size="md"
          iconClassName="h-4 w-4"
          onClick={onNextWeek}
          aria-label="Next week"
        />
      </div>

      {/* Day buttons */}
      <div className="flex gap-1 px-2 pb-3">
        {days.map((dateStr, index) => {
          const date = dateParseISO(dateStr);
          const isSelected = index === selectedDayIndex;
          const dayData = agenda?.[dateStr];
          const timeBlocks = getTimeBlockSegments(dayData?.schedules);
          const hasSchedules = timeBlocks.some(Boolean);
          const scheduleDots = getScheduleDots(dayData?.schedules);

          return (
            <button
              key={dateStr}
              onClick={() => onSelectDay(index)}
              className={cn(
                "flex flex-1 flex-col items-center gap-0.5 rounded-xl py-2 transition-colors",
                isSelected ? "bg-primary text-white" : "text-neutral-700"
              )}
            >
              <span className="font-medium text-[10px] uppercase leading-tight opacity-70">
                {format(date, "EEE")}
              </span>
              <span
                className={cn(
                  "font-semibold text-base leading-tight",
                  isSelected ? "text-white" : "text-neutral-900"
                )}
              >
                {format(date, "d")}
              </span>

              {/* Time blocks */}
              <div className="mt-1.5">
                {hasSchedules ? (
                  <TimeBlockBar segments={timeBlocks} isSelected={isSelected} />
                ) : (
                  <div className="h-1" />
                )}
              </div>

              {/* Schedule dots (one per reservation, colored by type) */}
              <div className="mt-1.5 flex items-center gap-0.5">
                {scheduleDots.length > 0
                  ? scheduleDots.map((dotColor, i) => (
                      <div
                        key={`dot-${dateStr}-${i}`}
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          isSelected ? "bg-white" : dotColor
                        )}
                      />
                    ))
                  : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TimeBlockBar: React.FC<{
  segments: boolean[];
  isSelected: boolean;
}> = ({ segments, isSelected }) => {
  return (
    <div className="flex gap-px">
      {segments.map((filled, i) => (
        <div
          key={`seg-${SEGMENT_BOUNDARIES[i]}`}
          className={cn(
            "h-1 w-2 rounded-[1px]",
            filled
              ? isSelected
                ? "bg-white"
                : "bg-primary"
              : isSelected
                ? "bg-white/25"
                : "bg-neutral-200"
          )}
        />
      ))}
    </div>
  );
};
